import { useEffect, useState } from 'react';
import {
  isStandalone, getDevice, tryOpenApp,
  isPwaInstalledIos, markPwaInstalled,
  detectOS, getDownloadUrl, getOSLabel,
  type AppDownloads,
} from '../lib/installDetect';

interface Props {
  onFinished: () => void;
  installPrompt?: BeforeInstallPromptEvent | null;
}

declare global {
  interface BeforeInstallPromptEvent extends Event {
    prompt(): Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
  }
}

const APP_SCHEME = 'velora';
const APP_COOKIE = 'velora_pwa';
const WORKER = 'https://velora-download.illluha-evgeniy.workers.dev';

const DOWNLOADS: AppDownloads = {
  android: `${WORKER}/download`,
  windows: `${WORKER}/download`,
  mac:     `${WORKER}/download`,
  linux:   `${WORKER}/download`,
};

type Gate =
  | 'choice'        // initial — "Open in App / Continue"
  | 'trying'        // Android/Desktop: calling tryOpenApp(), 2s spinner
  | 'not-installed' // scheme failed → show download
  | 'ios-open'      // iOS + PWA installed → "find icon on home screen"
  | 'ios-install';  // iOS + not installed → Add to Home Screen guide

const EQ_BARS = [
  { min: 4, max: 18, dur: 0.6, delay: 0.0 },
  { min: 6, max: 26, dur: 0.8, delay: 0.1 },
  { min: 3, max: 14, dur: 0.5, delay: 0.2 },
  { min: 8, max: 30, dur: 0.9, delay: 0.05 },
  { min: 4, max: 22, dur: 0.7, delay: 0.3 },
  { min: 6, max: 16, dur: 0.55, delay: 0.15 },
  { min: 3, max: 28, dur: 0.85, delay: 0.25 },
  { min: 5, max: 20, dur: 0.65, delay: 0.0 },
  { min: 4, max: 12, dur: 0.5, delay: 0.2 },
  { min: 7, max: 24, dur: 0.75, delay: 0.1 },
  { min: 3, max: 18, dur: 0.6, delay: 0.3 },
  { min: 6, max: 32, dur: 0.95, delay: 0.05 },
  { min: 4, max: 16, dur: 0.7, delay: 0.15 },
  { min: 5, max: 22, dur: 0.8, delay: 0.25 },
  { min: 3, max: 14, dur: 0.55, delay: 0.0 },
];

export default function SplashScreen({ onFinished, installPrompt }: Props) {
  const [phase, setPhase] = useState<'enter' | 'out'>('enter');
  const [gate, setGate] = useState<Gate | null>(null);
  const device = getDevice();
  const os = detectOS();
  const downloadUrl = getDownloadUrl(DOWNLOADS, os);
  const osLabel = getOSLabel(os);

  useEffect(() => {
    if (isStandalone()) markPwaInstalled(APP_COOKIE);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      if (isStandalone()) {
        // Already inside the app — no gate needed
        exit();
        return;
      }

      if (device === 'ios') {
        setGate(isPwaInstalledIos(APP_COOKIE) ? 'ios-open' : 'ios-install');
      } else {
        // Android APK or Desktop Electron — always show choice
        setGate('choice');
      }
    }, 3200);
    return () => clearTimeout(t);
  }, [device]); // eslint-disable-line react-hooks/exhaustive-deps

  const exit = () => {
    setGate(null);
    setPhase('out');
    setTimeout(onFinished, 600);
  };

  const handleOpenInApp = async () => {
    setGate('trying');
    const result = await tryOpenApp(APP_SCHEME);
    if (result === 'not-installed') {
      setGate('not-installed');
    }
    // 'opened' → OS opened the app, browser stays open — just wait
  };

  const handleInstallPwa = async () => {
    if (!installPrompt) return;
    await installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === 'accepted') exit();
  };

  // ── LOGO ────────────────────────────────────────────────────────────────────
  const Logo = ({ size = 72 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 90 90" style={{ marginBottom: 16 }}>
      <rect width="90" height="90" rx="20" fill="#0a0820"/>
      <rect width="90" height="90" rx="20" fill="none" stroke="rgba(83,74,183,0.5)" strokeWidth="0.8"/>
      <polyline points="14,22 45,68 76,22" fill="none" stroke="#534AB7" strokeWidth="7"
        strokeLinecap="round" strokeLinejoin="round" opacity="0.5"/>
      <polyline points="14,22 45,68 76,22" fill="none" stroke="#EEEDFE" strokeWidth="2.5"
        strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const BtnPrimary = ({ onClick, children }: { onClick: () => void; children: React.ReactNode }) => (
    <button onClick={onClick} style={{
      width: '100%', maxWidth: 300, padding: '15px',
      borderRadius: 16,
      background: 'rgba(83,74,183,0.30)',
      backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)',
      border: '1px solid rgba(127,119,221,0.45)',
      color: '#EEEDFE', fontSize: 16, fontWeight: 700,
      letterSpacing: 1, cursor: 'pointer', marginBottom: 12,
      fontFamily: 'Syne, Georgia, sans-serif',
      boxShadow: '0 8px 28px rgba(83,74,183,0.3), inset 0 1px 0 rgba(255,255,255,0.12)',
    }}>
      {children}
    </button>
  );

  const BtnGhost = ({ onClick, children }: { onClick: () => void; children: React.ReactNode }) => (
    <button onClick={onClick} style={{
      background: 'rgba(255,255,255,0.04)',
      backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
      border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: 12,
      color: 'rgba(238,237,254,0.35)', fontSize: 13,
      cursor: 'pointer', padding: '10px 24px',
      fontFamily: 'Plus Jakarta Sans, sans-serif',
    }}>
      {children}
    </button>
  );

  const WordMark = () => (
    <>
      <p style={{ fontFamily: 'Syne, Georgia, sans-serif', fontSize: 22, letterSpacing: 8, color: '#EEEDFE', margin: '0 0 4px', fontWeight: 700 }}>VELORA</p>
      <p style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: 10, letterSpacing: 3, color: 'rgba(174,169,236,0.45)', margin: '0 0 36px' }}>VOCAL TRAINING</p>
    </>
  );

  const overlayStyle: React.CSSProperties = {
    position: 'fixed', inset: 0, zIndex: 10,
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    background: 'linear-gradient(160deg, #050210 0%, #0a0820 100%)',
    padding: '32px 24px',
  };

  // ── GATE: choice (Android + Desktop) ────────────────────────────────────────
  if (gate === 'choice') {
    return (
      <div style={overlayStyle}>
        <Logo/>
        <WordMark/>
        <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.45)', textAlign: 'center', marginBottom: 28, lineHeight: 1.6, maxWidth: 260 }}>
          {device === 'desktop'
            ? 'Open Velora in the desktop app for the full experience.'
            : 'Open Velora in the app for the best experience.'}
        </p>
        <BtnPrimary onClick={handleOpenInApp}>
          Open in Velora
        </BtnPrimary>
        <BtnGhost onClick={exit}>Continue in browser</BtnGhost>
      </div>
    );
  }

  // ── GATE: trying to open via scheme (2s) ────────────────────────────────────
  if (gate === 'trying') {
    return (
      <div style={overlayStyle}>
        <Logo/>
        <WordMark/>
        <div style={{
          width: 36, height: 36, borderRadius: '50%',
          border: '2.5px solid rgba(83,74,183,0.3)',
          borderTopColor: '#7F77DD',
          animation: 'spin .8s linear infinite',
          marginBottom: 20,
        }}/>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)' }}>Opening Velora…</p>
        <BtnGhost onClick={exit} >Continue in browser</BtnGhost>
      </div>
    );
  }

  // ── GATE: app not installed ─────────────────────────────────────────────────
  if (gate === 'not-installed') {
    // Android, no APK URL → PWA-only
    if (device === 'android' && !downloadUrl) return (
      <div style={overlayStyle}>
        <Logo/>
        <WordMark/>
        <p style={{ fontSize: 14, color: 'rgba(255,255,255,.4)', textAlign: 'center', marginBottom: 20, lineHeight: 1.6, maxWidth: 260 }}>
          Add Velora to your home screen for the best experience.
        </p>
        {installPrompt && (
          <BtnPrimary onClick={handleInstallPwa}>⚡ Add to Home Screen</BtnPrimary>
        )}
        <BtnGhost onClick={exit}>Continue in browser</BtnGhost>
      </div>
    );

    // Android → rich APK card with steps + PWA fallback
    if (device === 'android') {
      return (
        <div style={{ ...overlayStyle, padding: '40px 20px 24px', overflowY: 'auto' }}>
          <Logo/>
          <WordMark/>
          <p style={{ fontSize: 15, fontWeight: 600, color: '#fff', marginBottom: 5 }}>Get the Velora app</p>
          <p style={{ fontSize: 12, color: 'rgba(255,255,255,.4)', textAlign: 'center', lineHeight: 1.5, marginBottom: 18, maxWidth: 270 }}>
            The APK unlocks full microphone access, offline mode and better performance.
          </p>

          {/* APK card */}
          <div style={{
            width: '100%', borderRadius: 16, padding: '20px 16px 16px',
            background: 'rgba(83,74,183,.12)', border: '1.5px solid rgba(83,74,183,.45)',
            position: 'relative', marginBottom: 10,
          }}>
            {/* Recommended badge */}
            <div style={{
              position: 'absolute', top: -11, left: 14,
              background: '#534AB7', color: '#EEEDFE',
              fontSize: 9, fontWeight: 700, letterSpacing: 1.5,
              padding: '3px 10px', borderRadius: 100,
            }}>RECOMMENDED</div>

            <p style={{ fontSize: 14, fontWeight: 700, color: '#fff', marginBottom: 8 }}>📦 Android APK</p>

            {/* Feature chips */}
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 14 }}>
              {['✓ Microphone', '✓ Offline', '✓ Full screen'].map(f => (
                <span key={f} style={{
                  fontSize: 10, padding: '3px 8px', borderRadius: 6,
                  background: 'rgba(74,222,128,.1)', color: '#4ade80',
                  border: '1px solid rgba(74,222,128,.2)',
                }}>{f}</span>
              ))}
            </div>

            {/* Download button */}
            {downloadUrl && (
              <a href={downloadUrl} style={{
                display: 'block', width: '100%', padding: '13px',
                borderRadius: 12, textDecoration: 'none', textAlign: 'center',
                background: 'linear-gradient(135deg,#534AB7,#7F77DD)',
                color: '#EEEDFE', fontSize: 15, fontWeight: 700,
                marginBottom: 16,
              }}>⬇ Download APK</a>
            )}

            {/* Steps */}
            <p style={{ fontSize: 10, letterSpacing: 1.5, textTransform: 'uppercase', color: 'rgba(255,255,255,.3)', marginBottom: 8 }}>How to install</p>
            {[
              'Tap Download APK above and wait for the file',
              <>Settings → Security → <strong style={{ color: 'rgba(255,255,255,.75)' }}>Unknown sources</strong> → enable for your browser</>,
              <>Open the downloaded file → <strong style={{ color: 'rgba(255,255,255,.75)' }}>Install</strong> → done!</>,
            ].map((text, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 7 }}>
                <div style={{
                  width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                  background: 'rgba(83,74,183,.25)', color: '#9B94E8',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, fontWeight: 700, marginTop: 1,
                }}>{i + 1}</div>
                <p style={{ fontSize: 12, color: 'rgba(255,255,255,.5)', lineHeight: 1.5 }}>{text}</p>
              </div>
            ))}
          </div>

          {/* Or divider */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', margin: '10px 0' }}>
            <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,.08)' }}/>
            <span style={{ fontSize: 10, color: 'rgba(255,255,255,.2)' }}>or quick install</span>
            <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,.08)' }}/>
          </div>

          {/* PWA option */}
          {installPrompt && (
            <button onClick={handleInstallPwa} style={{
              width: '100%', padding: '12px',
              borderRadius: 12, cursor: 'pointer',
              background: 'transparent',
              border: '1px solid rgba(83,74,183,.4)',
              color: 'rgba(155,148,232,.8)',
              fontSize: 13, fontWeight: 600, marginBottom: 6,
            }}>⚡ Add to Home Screen (PWA)</button>
          )}
          <p style={{ fontSize: 10, color: 'rgba(255,255,255,.2)', textAlign: 'center', marginBottom: 14, lineHeight: 1.4 }}>
            No download needed — some features may be limited
          </p>

          <BtnGhost onClick={exit}>Continue in browser</BtnGhost>
        </div>
      );
    }

    // Desktop → simple download
    return (
      <div style={overlayStyle}>
        <Logo/>
        <WordMark/>
        <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.45)', textAlign: 'center', marginBottom: 28, lineHeight: 1.6, maxWidth: 270 }}>
          Download Velora for {osLabel}:
        </p>
        {downloadUrl && (
          <a href={downloadUrl} style={{
            width: '100%', maxWidth: 300, padding: '15px',
            borderRadius: 14, border: 'none',
            background: 'linear-gradient(135deg,#534AB7,#7F77DD)',
            color: '#EEEDFE', fontSize: 15, fontWeight: 600,
            cursor: 'pointer', textDecoration: 'none',
            display: 'block', textAlign: 'center', marginBottom: 10,
          }}>
            ⬇ Download for {osLabel}
          </a>
        )}
        {installPrompt && (
          <BtnPrimary onClick={handleInstallPwa}>Install as Web App</BtnPrimary>
        )}
        <BtnGhost onClick={exit}>Continue in browser</BtnGhost>
      </div>
    );
  }

  // ── GATE: iOS + PWA already installed ───────────────────────────────────────
  if (gate === 'ios-open') {
    return (
      <div style={overlayStyle}>
        <Logo/>
        <WordMark/>
        <div style={{ fontSize: 48, marginBottom: 20 }}>📱</div>
        <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)', textAlign: 'center', lineHeight: 1.7, maxWidth: 270, marginBottom: 28 }}>
          Open <strong style={{ color: '#9B94E8' }}>Velora</strong> from your home screen to use the app.
        </p>
        <BtnGhost onClick={exit}>Continue in browser</BtnGhost>
      </div>
    );
  }

  // ── GATE: iOS + not installed → Add to Home Screen guide ────────────────────
  if (gate === 'ios-install') {
    return (
      <div style={overlayStyle}>
        <Logo size={60}/>
        <WordMark/>
        <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', marginBottom: 20, textAlign: 'center' }}>
          Add Velora to your iPhone:
        </p>
        {[
          { icon: '⬆️', text: 'Tap the Share button in Safari' },
          { icon: '➕', text: 'Tap "Add to Home Screen"' },
          { icon: '✅', text: 'Tap "Add" — done!' },
        ].map((s, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 14, width: '100%', maxWidth: 290, marginBottom: 12 }}>
            <div style={{
              width: 38, height: 38, borderRadius: 10, flexShrink: 0,
              background: 'rgba(83,74,183,0.18)', border: '1px solid rgba(83,74,183,0.3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
            }}>{s.icon}</div>
            <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', lineHeight: 1.4 }}>{s.text}</p>
          </div>
        ))}
        <BtnGhost onClick={exit}>Continue in browser</BtnGhost>
      </div>
    );
  }

  // ── SPLASH ANIMATION (unchanged) ────────────────────────────────────────────
  return (
    <>
      <style>{`
        @keyframes ringPulse {
          0%   { border-color: rgba(127,119,221,0); transform: translate(-50%,-50%) scale(0.7); }
          20%  { border-color: rgba(127,119,221,0.45); }
          100% { border-color: rgba(127,119,221,0); transform: translate(-50%,-50%) scale(1.05); }
        }
        @keyframes glowPulse {
          0%, 100% { opacity: 0.6; transform: translate(-50%,-50%) scale(1); }
          50%       { opacity: 1;   transform: translate(-50%,-50%) scale(1.12); }
        }
        @keyframes iconDrop {
          0%   { opacity: 0; transform: translate(-50%,-60%) scale(0.7); }
          60%  { opacity: 1; transform: translate(-50%,-47%) scale(1.06); }
          100% { opacity: 1; transform: translate(-50%,-50%) scale(1); }
        }
        @keyframes fadeUp {
          0%   { opacity: 0; transform: translateX(-50%) translateY(14px); }
          100% { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
        @keyframes eqBounce {
          0%   { opacity: 0.4; }
          100% { opacity: 0.85; }
        }
        @keyframes scan {
          0%   { top: -2px; opacity: 0; }
          5%   { opacity: 1; }
          95%  { opacity: 1; }
          100% { top: 100%; opacity: 0; }
        }
        @keyframes splashOut {
          to { opacity: 0; transform: scale(1.04); }
        }
        .splash-ring {
          position: absolute; border-radius: 50%;
          border: 1px solid rgba(127,119,221,0);
          left: 50%; top: 52%;
          transform: translate(-50%, -50%);
          animation: ringPulse 3.6s ease-out infinite;
        }
        .splash-icon {
          position: absolute; left: 50%; top: 38%;
          transform: translate(-50%, -50%);
          animation: iconDrop 1.2s cubic-bezier(0.22,1,0.36,1) forwards;
          opacity: 0;
        }
        .splash-wordmark {
          position: absolute; left: 50%; top: 63%;
          transform: translateX(-50%); text-align: center;
          animation: fadeUp 0.8s 1s cubic-bezier(0.22,1,0.36,1) forwards;
          opacity: 0;
        }
        .splash-eq {
          position: absolute; left: 50%; bottom: 72px;
          transform: translateX(-50%);
          display: flex; align-items: flex-end; gap: 3px;
          animation: fadeUp 0.8s 1.4s cubic-bezier(0.22,1,0.36,1) forwards;
          opacity: 0;
        }
        .splash-tagline {
          position: absolute; left: 50%; bottom: 48px;
          transform: translateX(-50%); white-space: nowrap;
          animation: fadeUp 0.8s 1.8s cubic-bezier(0.22,1,0.36,1) forwards;
          opacity: 0;
        }
        .splash-scanline {
          position: absolute; left: 0; top: -2px;
          width: 100%; height: 2px;
          background: linear-gradient(90deg, transparent 0%, rgba(174,169,236,0.18) 50%, transparent 100%);
          animation: scan 4s linear infinite;
        }
        .splash-out { animation: splashOut 0.6s ease forwards; }
      `}</style>

      <div
        className={`fixed inset-0 overflow-hidden ${phase === 'out' ? 'splash-out' : ''}`}
        style={{ background: 'linear-gradient(160deg, #0d0b29 0%, #050210 100%)' }}
      >
        <div className="splash-scanline" />

        {['top-4 left-4 border-t border-l','top-4 right-4 border-t border-r','bottom-8 left-4 border-b border-l','bottom-8 right-4 border-b border-r'].map((cls, i) => (
          <div key={i} className={`absolute w-3.5 h-3.5 ${cls}`} style={{ borderColor: 'rgba(83,74,183,0.5)' }} />
        ))}

        {[60, 110, 170, 230, 290].map((size, i) => (
          <div key={i} className="splash-ring" style={{ width: size, height: size, animationDelay: `${i * 0.5}s` }} />
        ))}

        <div style={{
          position: 'absolute', width: 140, height: 100, borderRadius: '50%',
          background: 'radial-gradient(ellipse, rgba(127,119,221,0.35) 0%, transparent 70%)',
          left: '50%', top: '52%', transform: 'translate(-50%,-50%)',
          animation: 'glowPulse 2.4s ease-in-out infinite',
        }} />

        <div className="splash-icon">
          <svg width="90" height="90" viewBox="0 0 90 90">
            <defs>
              <linearGradient id="sv" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#EEEDFE"/>
                <stop offset="100%" stopColor="#7F77DD"/>
              </linearGradient>
              <radialGradient id="dg" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#EEEDFE"/>
                <stop offset="100%" stopColor="#7F77DD" stopOpacity="0"/>
              </radialGradient>
              <filter id="fg"><feGaussianBlur stdDeviation="3"/></filter>
            </defs>
            <rect width="90" height="90" rx="20" fill="#0a0820"/>
            <rect width="90" height="90" rx="20" fill="none" stroke="rgba(83,74,183,0.5)" strokeWidth="0.8"/>
            <polyline points="14,22 45,68 76,22" fill="none" stroke="#7F77DD" strokeWidth="14" strokeLinecap="round" strokeLinejoin="round" opacity="0.2" filter="url(#fg)"/>
            <polyline points="14,22 45,68 76,22" fill="none" stroke="#534AB7" strokeWidth="7" strokeLinecap="round" strokeLinejoin="round" opacity="0.5"/>
            <polyline points="14,22 45,68 76,22" fill="none" stroke="url(#sv)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            <line x1="8" y1="68" x2="82" y2="68" stroke="#7F77DD" strokeWidth="0.8" strokeOpacity="0.4"/>
            <circle cx="14" cy="22" r="4" fill="url(#dg)" opacity="0.6"/><circle cx="14" cy="22" r="2" fill="white"/>
            <circle cx="76" cy="22" r="4" fill="url(#dg)" opacity="0.6"/><circle cx="76" cy="22" r="2" fill="white"/>
            <circle cx="45" cy="68" r="5" fill="url(#dg)" opacity="0.8"/><circle cx="45" cy="68" r="2.5" fill="white"/>
            <line x1="4" y1="10" x2="14" y2="10" stroke="rgba(83,74,183,0.6)" strokeWidth="1"/>
            <line x1="4" y1="10" x2="4" y2="20" stroke="rgba(83,74,183,0.6)" strokeWidth="1"/>
            <line x1="76" y1="10" x2="86" y2="10" stroke="rgba(83,74,183,0.6)" strokeWidth="1"/>
            <line x1="86" y1="10" x2="86" y2="20" stroke="rgba(83,74,183,0.6)" strokeWidth="1"/>
          </svg>
        </div>

        <div className="splash-wordmark">
          <span style={{ fontFamily: 'Syne, Georgia, sans-serif', fontSize: 20, fontWeight: 700, letterSpacing: 8, color: '#EEEDFE', display: 'block' }}>VELORA</span>
          <span style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: 9, letterSpacing: 3, color: 'rgba(174,169,236,0.6)', display: 'block', marginTop: 4 }}>VOCAL TRAINING</span>
        </div>

        <div className="splash-eq">
          {EQ_BARS.map((b, i) => (
            <div key={i} style={{
              width: 3, background: 'rgba(174,169,236,0.5)', borderRadius: 2,
              animation: `eqBounce ${b.dur}s ${b.delay}s ease-in-out infinite alternate`,
              height: b.min,
            }}/>
          ))}
        </div>

        <div className="splash-tagline" style={{ fontFamily: 'Arial, sans-serif', fontSize: 8.5, letterSpacing: 2, color: 'rgba(83,74,183,0.7)' }}>
          Sing. Improve. Shine.
        </div>
      </div>
    </>
  );
}
